import { decodeEventLog, type Address, type TransactionReceipt } from 'viem';

import type { ApiTransaction } from 'types';

import { multiSigLockerAbi } from './bridgeService';

export function sourceEventKey(
  source: Pick<ApiTransaction, 'direction' | 'evmChainId' | 'sourceTxHash' | 'sourceEventIndex'>
): string | null {
  const { evmChainId, sourceTxHash, sourceEventIndex } = source;
  if (
    source.direction !== 'eth_to_casper' ||
    !sourceTxHash ||
    !Number.isSafeInteger(evmChainId) ||
    evmChainId! <= 0 ||
    !Number.isSafeInteger(sourceEventIndex) ||
    sourceEventIndex! < 0
  )
    return null;

  return `evm:${evmChainId}:${sourceTxHash.toLowerCase()}:${sourceEventIndex}`;
}

/** The UI submits one direct lock/burn. Resolve only one matching receipt event. */
export function receiptSourceEventIndex(
  receipt: TransactionReceipt,
  expected: {
    hash: string;
    locker: Address;
    eventName: 'Locked' | 'WrappedBurned';
    chainId: number;
    token: string;
    casperTokenHash?: string;
    sender: string;
    amount: bigint;
    recipient: string;
    recipientType: number;
  }
): number | undefined {
  if (
    receipt.status !== 'success' ||
    receipt.transactionHash?.toLowerCase() !== expected.hash.toLowerCase()
  )
    return;
  const matches = (receipt.logs ?? []).filter((log) => {
    if (
      log.removed ||
      log.address.toLowerCase() !== expected.locker.toLowerCase() ||
      log.transactionHash?.toLowerCase() !== expected.hash.toLowerCase() ||
      !Number.isSafeInteger(log.logIndex) ||
      log.logIndex! < 0
    )
      return false;
    try {
      const event = decodeEventLog({
        abi: multiSigLockerAbi,
        data: log.data,
        topics: log.topics,
        strict: true,
      });
      const args = event.args;
      const token = 'token' in args ? args.token : args.wrappedToken;

      return (
        event.eventName === expected.eventName &&
        args.chainId === BigInt(expected.chainId) &&
        token.toLowerCase() === expected.token.toLowerCase() &&
        (!('casperTokenHash' in args) || args.casperTokenHash.toLowerCase() === expected.casperTokenHash?.toLowerCase()) &&
        args.sender.toLowerCase() === expected.sender.toLowerCase() &&
        args.amount === expected.amount &&
        args.casperRecipient.toLowerCase() === expected.recipient.toLowerCase() &&
        args.casperRecipientType === expected.recipientType
      );
    } catch {
      return false;
    }
  });

  return matches.length === 1 ? matches[0].logIndex! : undefined;
}
